// The ?discord_auth=<status> outcomes the Discord OAuth callback can land on
// (api-surface 1.3, discord-identity-routes.ts), plus the messages the live
// desk shows for each (betaApi.ts useBetaAccess). `not_open` is the early
// access denial from memory aleph-early-access-allowlist-2026-08-10; it is
// absent from the inspected worktree, so it is mapped but flagged unverified.

export const DISCORD_AUTH_STATUSES = [
  'connected',
  'linked',
  'unlinked',
  'denied',
  'collision',
  'state_invalid',
  'provider_failed',
  'service_unavailable',
  'session_unavailable',
  'link_unavailable',
  'membership_unavailable',
  'bootstrap_password_replacement_required',
  'workspace_selection_required',
  'not_open',
] as const;

export type DiscordAuthStatus = (typeof DISCORD_AUTH_STATUSES)[number];

export type DiscordAuthOutcome = 'signed_in' | 'signed_out' | 'challenge' | 'denied';

export interface DiscordAuthDescription {
  readonly status: DiscordAuthStatus | 'unknown';
  readonly outcome: DiscordAuthOutcome;
  readonly message: string;
  // The desk stores a resume flag so a later password sign-in finishes the link.
  readonly resumeLinkAfterPasswordSignIn: boolean;
  // Needs GET /api/beta/auth/access-challenge before anything else.
  readonly requiresAccessChallenge: boolean;
  readonly verifiedOnLive: boolean;
}

const DESCRIPTIONS: Readonly<Record<DiscordAuthStatus, Omit<DiscordAuthDescription, 'status'>>> = {
  connected: {
    outcome: 'signed_in',
    message: 'Discord sign-in connected.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  linked: {
    outcome: 'signed_in',
    message: 'Discord sign-in linked to this account.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  unlinked: {
    outcome: 'signed_out',
    message: 'That Discord identity is not linked yet. Sign in with email and password to finish linking Discord automatically.',
    resumeLinkAfterPasswordSignIn: true,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  denied: {
    outcome: 'signed_out',
    message: 'Discord sign-in was cancelled. No account access changed.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  collision: {
    outcome: 'signed_out',
    message: 'That Discord identity is already linked to a different account. Sign in with the account that owns it, or use email and password.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  state_invalid: {
    outcome: 'signed_out',
    message: 'The Discord sign-in request was invalid, expired, or already used. Start again.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  provider_failed: {
    outcome: 'signed_out',
    message: 'Discord could not verify the sign-in. Try again or use email and password.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  service_unavailable: {
    outcome: 'signed_out',
    message: 'Discord sign-in is not available right now. Nothing changed. Try again later or use email and password.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  session_unavailable: {
    outcome: 'signed_out',
    message: 'Discord verified your identity, but Aleph could not open the workspace. Try again or use email and password.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  link_unavailable: {
    outcome: 'signed_out',
    message: 'Discord sign-in could not complete the account link. Nothing changed. Try again, or sign in with email and password.',
    resumeLinkAfterPasswordSignIn: true,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  membership_unavailable: {
    outcome: 'denied',
    message: 'This Discord identity has no workspace membership or open invitation. Ask the workspace owner for an invitation.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: true,
  },
  bootstrap_password_replacement_required: {
    outcome: 'challenge',
    message: 'Replace the temporary owner password before opening external workspace access.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: true,
    verifiedOnLive: true,
  },
  workspace_selection_required: {
    outcome: 'challenge',
    message: 'Choose the workspace to open.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: true,
    verifiedOnLive: true,
  },
  not_open: {
    outcome: 'denied',
    message: 'Early access is invitation only. This Discord identity is not on the list yet.',
    resumeLinkAfterPasswordSignIn: false,
    requiresAccessChallenge: false,
    verifiedOnLive: false,
  },
};

const UNKNOWN: Omit<DiscordAuthDescription, 'status'> = {
  outcome: 'signed_out',
  message: 'Sign in to open this workspace.',
  resumeLinkAfterPasswordSignIn: false,
  requiresAccessChallenge: false,
  verifiedOnLive: false,
};

export function isDiscordAuthStatus(value: string | null | undefined): value is DiscordAuthStatus {
  return typeof value === 'string' && (DISCORD_AUTH_STATUSES as readonly string[]).includes(value);
}

// An unrecognised status names itself. The generic "Sign in to open this
// workspace." told a reader nothing about what the callback actually carried,
// which is the one thing worth knowing when a new backend status appears. The
// value is bounded and quoted because it arrives from the query string.
const UNKNOWN_STATUS_MAX = 40;

export function describeDiscordAuth(status: string | null | undefined): DiscordAuthDescription {
  const trimmed = status?.trim() ?? '';
  if (isDiscordAuthStatus(trimmed)) return { status: trimmed, ...DESCRIPTIONS[trimmed] };
  const named = trimmed === ''
    ? 'The Discord callback carried no status.'
    : `The Discord callback carried "${trimmed.slice(0, UNKNOWN_STATUS_MAX)}${trimmed.length > UNKNOWN_STATUS_MAX ? '...' : ''}", which this build does not recognise.`;
  return { status: 'unknown', ...UNKNOWN, message: `${named} ${UNKNOWN.message}` };
}

// When the challenge itself has expired the desk shows a specific line
// (betaApi.ts discordMessages after a 401 on auth/me).
export function describeExpiredChallenge(status: DiscordAuthStatus): string | null {
  if (status === 'workspace_selection_required') return 'The workspace choice expired. Start Discord sign-in again.';
  if (status === 'bootstrap_password_replacement_required') {
    return 'The password replacement step expired. Sign in with the temporary password to start again.';
  }
  return null;
}
